import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Crown, Phone, Droplet, Zap, Shield, Clock, MapPin, Heart, Check } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import BookingModal from '@/components/BookingModal';

const HomePage = () => {
  const [showBooking, setShowBooking] = useState(false);

  const services = [
    {
      name: 'Myers Cocktail',
      icon: '🏆',
      subtitle: 'The original wellness blend',
      price: 210,
      description: 'The gold standard of IV therapy — a proprietary blend of magnesium, calcium, B-complex vitamins, and vitamin C developed by Dr. John Myers. Delivered directly into the bloodstream to boost immunity, elevate energy, ease fatigue, and support overall cellular wellness. A trusted foundation for chronic fatigue, migraines, seasonal illness, and general revitalization.'
    },
    {
      name: 'Hangover Drip',
      icon: '🍹',
      subtitle: 'Recovery, refined',
      price: 210,
      description: 'Rapid recovery for the morning after — 1L of lactated ringers to restore hydration and electrolytes, paired with anti-nausea and pain-relief medications, plus a B-vitamin complex to combat the depletion caused by alcohol. Bypasses the GI tract for immediate absorption, restoring you to normal in 30–45 minutes instead of hours.'
    },
    {
      name: 'Hydrate',
      icon: '💧',
      subtitle: 'Pure cellular rehydration',
      price: 135,
      description: 'The foundational IV therapy — 1L of medical-grade lactated ringers to rapidly rehydrate at the cellular level. Ideal for post-workout recovery, heat exhaustion, jet lag, illness recovery, or anyone needing to restore optimal fluid and electrolyte balance faster than water alone can provide.'
    },
    {
      name: 'Athletic Drip',
      icon: '💪',
      subtitle: 'Performance & recovery',
      price: 210,
      description: 'Engineered for athletes and active professionals — a strategic infusion of amino acids, B-vitamins, magnesium, and electrolytes to accelerate muscle recovery, replenish glycogen stores, reduce post-workout inflammation, and prepare the body for peak performance. Ideal before competitions or after intense training sessions.'
    },
    {
      name: 'Infusion Beauty',
      icon: '✨',
      subtitle: 'Radiance from within',
      price: 210,
      description: 'A luxurious blend targeting skin, hair, and nail vitality — high-dose vitamin C, biotin, and B-complex to boost collagen production, brighten complexion, strengthen keratin structures, and combat oxidative damage. Delivers antioxidants at concentrations impossible to achieve through oral supplementation.'
    },
    {
      name: 'Immune Drip',
      icon: '🛡️',
      subtitle: 'Frontline immune defense',
      price: 210,
      description: 'A high-dose vitamin C infusion (2–5g) combined with zinc, B-vitamins, and lysine to fortify your immune system at the cellular level. Ideal at the first sign of illness, before travel, during flu season, or anytime immune resilience is critical. Delivers antioxidant concentrations up to 100× higher than oral vitamin C.'
    },
    {
      name: 'Infusion Migraine',
      icon: '🧠',
      subtitle: 'Fast-acting relief',
      price: 210,
      description: 'Targeted relief for migraine and tension headaches — magnesium sulfate to relax blood vessels and calm nerve activity, combined with anti-inflammatory and anti-nausea medications and B-complex vitamins. Bypasses the digestive system (which is often shut down during migraines), providing relief in as little as 15–30 minutes.'
    }
  ];

  const benefits = [
    {
      icon: <MapPin className="w-8 h-8" />,
      title: 'We Come To You',
      description: 'Mobile service delivered to your home, office, or hotel'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'RN Administered',
      description: 'Professional registered nurse care with patient education'
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: 'Fast Results',
      description: '100% absorption - feel the difference in 30-45 minutes'
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: 'Premium Quality',
      description: 'Pharmaceutical-grade vitamins and minerals'
    }
  ];

  const handleLeadSubmit = async () => {}; // eslint-disable-line no-unused-vars
  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden min-h-[85vh] flex items-center">
        <video
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          data-testid="hero-video"
        >
          <source src="/hero-video.webm" type="video/webm" />
          <source src="/hero-video.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-black/20" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/10 to-black/30" />

        <div className="relative max-w-7xl mx-auto text-center w-full" data-testid="hero-section">
          <div className="mb-6 flex justify-center">
            <Crown className="w-16 h-16 text-yellow-400 pulse-gold" />
          </div>
          <h1 className="text-5xl md:text-7xl font-bold mb-6 drop-shadow-[0_4px_18px_rgba(0,0,0,0.95)]">
            <span className="gradient-text">Royal Treatment</span>
            <br />
            <span className="text-white">For Your Wellness</span>
          </h1>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mt-4">
            <button
              onClick={() => setShowBooking(true)}
              className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black px-8 py-4 rounded-full text-lg font-bold hover:from-yellow-500 hover:to-yellow-700 transition-all btn-gold-hover"
              data-testid="hero-book-now-btn"
            >
              Book Your Session
            </button>
            <a
              href="tel:727-503-2572"
              className="flex items-center space-x-2 border-2 border-yellow-400 text-yellow-400 px-8 py-4 rounded-full text-lg font-bold hover:bg-yellow-400 hover:text-black transition-all"
              data-testid="hero-call-btn"
            >
              <Phone className="w-5 h-5" />
              <span>Call Now</span>
            </a>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 px-4 bg-gradient-to-b from-black to-gray-900" data-testid="services-preview">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block text-yellow-400/80 text-xs uppercase tracking-[0.4em] mb-4 font-gothic">
              Our Menu
            </div>
            <h2 className="text-4xl md:text-5xl font-gothic mb-4">
              <span className="gradient-text">Our IV Cocktails</span>
            </h2>
            <p className="text-lg text-gray-400 font-gothic-italic">
              Premium vitamin infusions, crafted for every wellness goal
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {services.map((service, index) => (
              <div
                key={index}
                className="gothic-card"
                data-testid={`service-card-${index}`}
              >
                <div className="flex items-start justify-between">
                  <div className="gothic-icon">{service.icon}</div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-yellow-400/60 font-gothic">From</div>
                    <div className="text-2xl font-gothic text-yellow-400 font-semibold leading-none">
                      ${service.price}
                    </div>
                  </div>
                </div>
                <h3 className="gothic-heading text-xl mt-2">{service.name}</h3>
                <p className="gothic-subtitle">{service.subtitle}</p>
                <div className="gothic-divider" />
                <p className="gothic-body">{service.description}</p>
                <button
                  onClick={() => setShowBooking(true)}
                  className="gothic-cta hover:text-yellow-300"
                >
                  Book This Service →
                </button>
              </div>
            ))}
          </div>

          {/* IM Injections — shown alongside IV menu so clients see the full offering */}
          <div className="mt-20" data-testid="im-injections-section">
            <div className="text-center mb-12">
              <div className="inline-block text-yellow-400/80 text-xs uppercase tracking-[0.4em] mb-4 font-gothic">
                Also Available
              </div>
              <h2 className="text-3xl md:text-4xl font-gothic mb-3">
                <span className="gradient-text">IM Injections</span>
              </h2>
              <p className="text-lg text-gray-400 font-gothic-italic">
                Targeted intramuscular boosts — fast, focused, effective
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {[
                { name: 'B12 Energy Shot', icon: '⚡', price: 40, subtitle: 'Sustained natural energy', body: 'A concentrated dose of vitamin B12 supports red blood cell production, nervous system function, and steady, non-jittery energy — ideal for busy professionals, athletes, and anyone feeling fatigued.' },
                { name: 'Vitamin D3 Shot', icon: '☀️', price: 40, subtitle: 'Mood & immune support', body: 'The "sunshine vitamin" delivered directly to your bloodstream. D3 strengthens immunity, supports bone health, improves mood, and helps regulate hormone balance — especially valuable during cloudy months or if you spend long hours indoors.' },
                { name: 'Glutathione', icon: '🌿', price: 40, subtitle: 'The master antioxidant', body: 'Glutathione is your body\'s most powerful natural antioxidant — a peptide produced in every cell to neutralize free radicals, support liver detoxification, brighten skin, reduce inflammation, and defend against oxidative stress. Intramuscular delivery bypasses the digestive tract for maximum absorption and cellular impact. Popular for anti-aging, immune resilience, energy, and radiant skin.' },
                { name: 'Arginine Injection', icon: '💪', price: 40, subtitle: 'Circulation & recovery', body: 'L-Arginine is an amino acid that converts to nitric oxide, expanding blood vessels for improved circulation, oxygen delivery, and cardiovascular support. Athletes use it for endurance and recovery; others for heart health and healing.' },
                { name: 'Lysine Injection', icon: '🛡️', price: 40, subtitle: 'Immune & cold sore defense', body: 'An essential amino acid your body cannot produce on its own. Lysine supports immune function, collagen formation, calcium absorption, and is well-known for helping suppress cold sore (HSV-1) outbreaks.' },
                { name: 'Pyridoxine (B6)', icon: '🧠', price: 40, subtitle: 'Mental clarity & balance', body: 'Vitamin B6 is essential for brain development, neurotransmitter production (serotonin, dopamine), hormone regulation, and hemoglobin formation — supporting mood, focus, and overall nervous system health.' },
                { name: 'Zofran', icon: '💊', price: 50, subtitle: 'Anti-nausea relief', body: 'A prescription-strength anti-nausea medication (ondansetron) that provides fast, effective relief from nausea and vomiting caused by hangovers, migraines, motion sickness, or GI upset.' },
                { name: 'Ketorolac', icon: '🎯', price: 50, subtitle: 'Targeted pain relief', body: 'A non-narcotic anti-inflammatory (Toradol) that provides powerful relief for muscle pain, joint inflammation, migraines, and post-workout soreness — without the drowsiness or dependency risks of opioids.' },
              ].map((inj, idx) => (
                <div
                  key={inj.name}
                  className="gothic-card"
                  style={{ padding: '22px 20px' }}
                  data-testid={`im-injection-card-${idx}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="gothic-icon" style={{ width: 44, height: 44, fontSize: 20, marginBottom: 14 }}>
                      {inj.icon}
                    </div>
                    <div className="text-right">
                      <div className="text-[9px] uppercase tracking-[0.2em] text-yellow-400/60 font-gothic">Price</div>
                      <div className="text-xl font-gothic text-yellow-400 font-semibold leading-none">
                        ${inj.price}
                      </div>
                    </div>
                  </div>
                  <h3 className="gothic-heading text-lg leading-tight mt-1">{inj.name}</h3>
                  <p className="gothic-subtitle text-sm">{inj.subtitle}</p>
                  {inj.body && (
                    <p className="gothic-body text-[13px] leading-relaxed mt-1 mb-3">
                      {inj.body}
                    </p>
                  )}
                  <button
                    onClick={() => setShowBooking(true)}
                    className="gothic-cta hover:text-yellow-300 text-[11px]"
                  >
                    Book This Shot →
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center mt-12">
            <Link
              to="/services"
              className="inline-block border-2 border-yellow-400 text-yellow-400 px-8 py-3 rounded-full font-bold hover:bg-yellow-400 hover:text-black transition-all"
              data-testid="view-all-services-btn"
            >
              View All Services
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 bg-gray-900" data-testid="why-choose-us">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="gradient-text">Why EliteIV Lounge?</span>
            </h2>
            <p className="text-xl text-gray-400">Premium care that comes to you</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="text-center"
                data-testid={`benefit-card-${index}`}
              >
                <div className="bg-gradient-to-br from-yellow-400/20 to-yellow-600/20 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4 text-yellow-400">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold text-yellow-400 mb-2">{benefit.title}</h3>
                <p className="text-gray-300">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="py-20 px-4 bg-black" data-testid="about-preview">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="gradient-text">Expert Care,</span>
                <br />
                <span className="text-white">Delivered</span>
              </h2>
              <p className="text-gray-300 text-lg mb-6">
                At EliteIV Lounge, we believe that when you're not feeling your best, you shouldn't have to go anywhere. That's why we bring premium IV therapy directly to you.
              </p>
              <p className="text-gray-300 text-lg mb-6">
                As a Registered Nurse, I'm passionate about patient education - especially helping my patients understand the significant difference between oral supplementation and the intravenous approach, and how the superior efficacy, enhanced absorption, and prolonged benefits of intravenous therapy provide a stronger foundation for overall health, wellness, and long-term physiological optimization.
              </p>
              <Link
                to="/about"
                className="inline-block bg-gradient-to-r from-yellow-400 to-yellow-600 text-black px-8 py-3 rounded-full font-bold hover:from-yellow-500 hover:to-yellow-700 transition-all"
                data-testid="learn-more-btn"
              >
                Learn More About Us
              </Link>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-yellow-400/20 to-transparent border border-yellow-500/30 rounded-3xl p-8">
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <div className="bg-yellow-400/20 p-3 rounded-xl">
                      <Shield className="w-6 h-6 text-yellow-400" />
                    </div>
                    <div>
                      <h4 className="font-bold text-white">Licensed RN</h4>
                      <p className="text-gray-400 text-sm">Professional medical expertise</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="bg-yellow-400/20 p-3 rounded-xl">
                      <Droplet className="w-6 h-6 text-yellow-400" />
                    </div>
                    <div>
                      <h4 className="font-bold text-white">100% Absorption</h4>
                      <p className="text-gray-400 text-sm">Direct nutrient delivery</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="bg-yellow-400/20 p-3 rounded-xl">
                      <Zap className="w-6 h-6 text-yellow-400" />
                    </div>
                    <div>
                      <h4 className="font-bold text-white">Fast Results</h4>
                      <p className="text-gray-400 text-sm">Feel better in 30-45 minutes</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final Booking CTA — opens the REAL BookingModal (with consent & multi-select) */}
      <section className="py-20 px-4 bg-gradient-to-b from-gray-900 to-black" data-testid="final-booking-cta">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-block text-yellow-400/80 text-xs uppercase tracking-[0.4em] mb-4 font-gothic">
            Self-Book In Minutes
          </div>
          <h2 className="text-4xl md:text-5xl font-gothic mb-6">
            <span className="gradient-text">Ready to Feel Your Best?</span>
          </h2>
          <p className="text-lg text-gray-300 mb-3 font-gothic-italic">
            Reserve your IV therapy or IM injection in just a few taps — consent, scheduling, and confirmation handled instantly.
          </p>
          <p className="text-sm text-gray-500 mb-10">
            A licensed provider will follow up via text to schedule a brief telehealth consult and medically clear you for treatment.
          </p>

          <div className="gothic-card max-w-xl mx-auto" style={{ padding: '36px 28px' }}>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6 text-left">
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                <span className="text-gray-300 text-sm font-gothic-italic">Pick date &amp; time</span>
              </div>
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                <span className="text-gray-300 text-sm font-gothic-italic">Combine IV + IM</span>
              </div>
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                <span className="text-gray-300 text-sm font-gothic-italic">Digital consent</span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setShowBooking(true)}
              className="w-full bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-gothic font-bold py-4 rounded-xl hover:from-yellow-500 hover:to-yellow-700 transition-all tracking-[0.15em] uppercase text-sm"
              data-testid="final-book-now-btn"
            >
              Book Your Treatment
            </button>

            <p className="mt-4 text-xs text-gray-500">
              Prefer to talk to a person?{' '}
              <a href="tel:727-503-2572" className="text-yellow-400 hover:underline">
                Call 727-503-2572
              </a>
            </p>
          </div>
        </div>
      </section>

      <Footer />
      <BookingModal isOpen={showBooking} onClose={() => setShowBooking(false)} />
    </div>
  );
};

export default HomePage;
